import React, { useState, useEffect } from "react";
import {
  Snackbar,
  Alert,
  Box,
  Typography,
  TextField,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  Grid,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AgGridReact } from "ag-grid-react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS
import "ag-grid-community/styles/ag-theme-alpine.css";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import PropTypes from "prop-types";
import { StyledButton } from "components/StyledComponents/styledComponents";
import { DeleteButton } from "components/StyledComponents/styledComponents";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const PipeLineReport = () => {
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState(null);
  const jwt_token = Cookie.get("jwt_token");
  const [loading, setLoading] = useState(true);
  const [rowsWithSeparators, setRowsWithSeparators] = useState([]);
  const [filterText, setFilterText] = useState("");
  useEffect(() => {
    const processedRows = generateRowsWithSeparators(rows); // rows is your original data
    setRowsWithSeparators(processedRows);
  }, [rows]);
  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilterText(value);

    // Custom filter on your rows
    const filteredRows = rows.filter((row) =>
      row.status.toLowerCase().includes(value.toLowerCase())
    );

    // Now regenerate separator rows
    const processedRows = generateRowsWithSeparators(filteredRows);
    setRowsWithSeparators(processedRows);
  };
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Add leading zero for months less than 10
    const day = String(today.getDate()).padStart(2, "0"); // Add leading zero for days less than 10
    return `${year}-${month}-${day}`;
  };
  const [startDate, setStartDate] = useState(getTodayDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date)) return "";

    const options = { year: "numeric", month: "short", day: "2-digit" };
    let formattedDate = date.toLocaleDateString("en-GB", options);

    // Remove the comma
    formattedDate = formattedDate.replace(",", "");

    const [day, month, year] = formattedDate.split(" ");
    if (!day || !month || !year) return "";

    const pascalMonth = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
    return `${day}-${pascalMonth}-${year}`;
  };
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${URLs.pipelineGet}?startDate=${startDate}&endDate=${endDate}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });
      if (!res.ok) {
        throw new Error("Failed to fetch data");
      }
      const data = await res.json();
      setRows(data);
    } catch (err) {
      console.error("Error loading data:", err);
      setSnackbar({ children: "Failed to load data", severity: "error" });
    }
    setLoading(false);
  };

  const addNewRow = () => {
    const newRow = {
      date: getTodayDate(), // Set current date as default
      loginId: "",
      customerName: "",
      bankName: "",
      productCode: "",
      loanAmount: 0,
      code: "",
      employeeName: "",
      status: "",
      bankerName: "",
    };
    setRows((prevRows) => [...prevRows, newRow]); // Add the new row at the top
  };

  const handlePostPutRow = async (newRowData) => {
    const postData = {
      ...newRowData,
      loanAmount: parseFloat(newRowData.loanAmount) || 0,
      pipeLineId: newRowData.pipeLineId || 0,
    };
    setLoading(true);
    try {
      const isUpdate = Boolean(newRowData.pipeLineId); // If pipeLineId exists, it's an update (PUT)
      const url = isUpdate ? `${URLs.pipelinePut}/${newRowData.pipeLineId}` : URLs.pipelinePost;

      const res = await fetch(url, {
        method: isUpdate ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(postData),
      });

      if (!res.ok) {
        throw new Error(isUpdate ? "Failed to update row" : "Failed to add new row");
      }

      const data = await res.json();
      setSnackbar({ children: isUpdate ? "Row Updated" : "Row Added", severity: "success" });
      fetchData(); // Refresh data after adding/updating
      return data;
    } catch (err) {
      console.error("Error saving row:", err);
      setSnackbar({ children: "Failed to save row ❌", severity: "error" });
      throw err;
    }
    setLoading(false);
  };
  const handleDeleteRow = async (rowData, rowIndex) => {
    // If no pipeLineId, it's a newly added row ➔ remove by index from rows
    if (!rowData.pipeLineId) {
      setRows((prevRows) => prevRows.filter((_, index) => index !== rowIndex));
      setSnackbar({ children: "Row Removed", severity: "success" });
      return;
    }

    // Else do the backend API delete
    setLoading(true);
    try {
      const url = `${URLs.pipeLineDelete}/${rowData.pipeLineId}`;

      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to delete row");
      }

      const data = await res.json();
      setSnackbar({ children: "Row Deleted", severity: "success" });
      fetchData(); // Refresh rows from backend
      return data;
    } catch (err) {
      setSnackbar({ children: "Failed to Delete Row", severity: "error" });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      headerName: "SL. NO",
      valueGetter: (params) => {
        if (params.data?.rowType === "date-separator") return "";
        let serial = 0;
        let currentDate = params.data.date;
        for (let i = 0; i <= params.node.rowIndex; i++) {
          const rowNode = params.api.getDisplayedRowAtIndex(i);
          if (rowNode?.data?.rowType !== "date-separator" && rowNode?.data?.date === currentDate) {
            serial++;
          }
        }
        return serial;
      },
      cellStyle: { fontWeight: "bold" },
      editable: false,
      maxWidth: 80, // keep SL NO fixed small
    },
    {
      headerName: "Date",
      field: "date",
      filter: true,
      editable: false,
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) => {
        if (params.data?.rowType === "date-separator") return ""; // We'll handle display in cellRenderer
        return formatDate(params.value);
      },
      cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
      cellRenderer: (params) => {
        if (params.data?.rowType === "date-separator") {
          return (
            <div
              style={{
                backgroundColor: "#333333",
                color: "black",
                borderRadius: "8px",
                padding: "4px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                color: "rgb(240, 234, 234)",
              }}
            >
              {formatDate(params.value)}
            </div>
          );
        }
        return formatDate(params.value);
      },
    },
    {
      headerName: "Login ID",
      field: "loginId",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
      cellRenderer: (params) => (params.data?.rowType === "date-separator" ? "" : params.value),
    },
    {
      headerName: "Customer Name",
      field: "customerName",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 150,
      cellRenderer: (params) => (params.data?.rowType === "date-separator" ? "" : params.value),
    },
    {
      headerName: "Bank",
      field: "bankName",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 120,
      cellRenderer: (params) => (params.data?.rowType === "date-separator" ? "" : params.value),
    },
    {
      headerName: "Product",
      field: "productCode",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
      cellRenderer: (params) => (params.data?.rowType === "date-separator" ? "" : params.value),
    },
    {
      headerName: "Loan Amount",
      field: "loanAmount",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 130,
      valueFormatter: (params) => {
        if (params.data?.rowType === "date-separator") return "";
        const value = Number(params.value) || 0;
        return `₹ ${value.toLocaleString("en-IN")}`;
      },
      cellStyle: (params) =>
        params.data?.rowType === "date-separator" ? {} : { textAlign: "right", fontWeight: "bold" },
    },
    {
      headerName: "Code",
      field: "code",
      editable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
      cellRenderer: (params) => (params.data?.rowType === "date-separator" ? "" : params.value),
    },
    {
      headerName: "Employee Name",
      field: "employeeName",
      filter: true,
      editable: true,
      flex: 1,
      minWidth: 150,
      cellRenderer: (params) => (params.data?.rowType === "date-separator" ? "" : params.value),
    },
    {
      headerName: "Status",
      field: "status",
      filter: true,
      editable: true,
      flex: 1,
      minWidth: 180, // responsive
      cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
      cellRenderer: (params) => {
        if (params.data?.rowType === "date-separator") return "";
        const isCancelled = params.value?.toLowerCase() === "cancelled";
        const bgColor = isCancelled ? "#f44336" : "#ff9800";
        const textColor = "#fff";
        return (
          <div
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 8px",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: "bold",
              textAlign: "center",
              minWidth: "80px",
              height: "30px",
            }}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      headerName: "Banker Name",
      field: "bankerName",
      filter: true,
      editable: true,
      flex: 1,
      minWidth: 150,
      cellRenderer: (params) => (params.data?.rowType === "date-separator" ? "" : params.value),
    },
    {
      headerName: "Action",
      maxWidth: 160, // keep Action fixed small
      cellStyle: { textAlign: "center" },
      cellRenderer: (params) => {
        if (params.data?.rowType === "date-separator") return "";
        return (
          <>
            <StyledButton
              variant="contained"
              color="primary"
              type="button"
              sx={{ height: "35px" }}
              onClick={() => handlePostPutRow(params.data)}
            >
              Save
            </StyledButton>
            <DeleteButton
              type="button"
              onClick={() => handleDeleteRow(params.data, params.node.rowIndex)}
            >
              <DeleteIcon />
            </DeleteButton>
          </>
        );
      },
    },
  ];

  const DateSeparatorRenderer = (props) => {
    return (
      <div
        style={{
          textAlign: "center",
          width: "100%",
          fontWeight: "bold",
          padding: "10px",
          backgroundColor: "#f0f0f0", // Light grey
          borderBottom: "1px solid #ccc",
          borderTop: "1px solid #ccc",
        }}
      >
        ─────── {formatDate(props.data.date)} ───────
      </div>
    );
  };

  DateSeparatorRenderer.propTypes = {
    data: PropTypes.shape({
      date: PropTypes.string.isRequired,
    }).isRequired,
  };
  const generateRowsWithSeparators = (dataRows) => {
    const grouped = {};
    dataRows.forEach((row) => {
      const date = row.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(row);
    });
    // Now build rowsWithSeparators
    const result = [];
    Object.keys(grouped)
      .sort()
      .forEach((date) => {
        // Add date separator row
        result.push({ rowType: "date-separator", date });

        // Add actual rows for this date
        result.push(...grouped[date]);
      });
    return result;
  };
  const handleDownload = () => {
    if (!rows || rows.length === 0) {
      setSnackbar({ children: "No data available to download.", severity: "error" });
      return;
    }

    const header = Object.keys(rows[0]);
    const worksheetData = [header, ...rows.map(Object.values)];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Apply bold style to headers (row 1 = index 0)
    header.forEach((col, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index }); // row 0 = header row
      if (!worksheet[cellRef]) return;

      worksheet[cellRef].s = {
        font: {
          bold: true,
        },
      };
    });

    // Optional: set default column widths
    worksheet["!cols"] = header.map(() => ({ wch: 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true, // required for styles to work
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(blob, "pipeline.xlsx");
  };
  return (
    <>
      <Dialog open={loading} onClose={() => {}} aria-labelledby="loading-dialog-title">
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <Box sx={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column", pt: 1 }}>
        <Paper sx={{ padding: 2, mb: 1 }}>
          <Typography variant="h6" mb={1} gutterBottom>
            PipeLine Sheet
          </Typography>
          <Grid container spacing={2} sx={{ mb: 0 }}>
            <Grid item xs={12} sm={12} md={9}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                  mb: 1, // Slightly more space below the box
                }}
              >
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <StyledButton variant="contained" color="primary" onClick={fetchData}>
                  Get Data
                </StyledButton>
                <StyledButton variant="contained" color="secondary" onClick={addNewRow}>
                  Add New Row
                </StyledButton>
                <StyledButton onClick={handleDownload}>Download</StyledButton>
              </Box>
            </Grid>

            <Grid item xs={12} sm={12} md={3}>
              <TextField
                type="text"
                value={filterText}
                onChange={handleFilterChange}
                placeholder="Filter by Status"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ flexGrow: 1, width: "100%", display: "flex", flexDirection: "column" }}>
          <div className="ag-theme-alpine" style={{ flex: 1, width: "100%" }}>
            <AgGridReact
              rowData={rowsWithSeparators}
              columnDefs={columns}
              isFullWidthRow={(params) => params.data?.rowType === "date-separator"}
              fullWidthCellRenderer={DateSeparatorRenderer}
            />
          </div>
        </Box>
        {!!snackbar && (
          <Snackbar
            open
            onClose={() => setSnackbar(null)}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              {...snackbar}
              onClose={() => setSnackbar(null)}
              sx={{
                backgroundColor: snackbar?.severity === "error" ? "#cc0000" : undefined, // Light red for error
                color: snackbar?.severity === "error" ? "#ffffff" : undefined, // Dark red text (optional)
                fontWeight: "bold",
              }}
            />
          </Snackbar>
        )}
      </Box>
    </>
  );
};

export default PipeLineReport;
